import { DEWEY_SUBJECT_MAPPING } from "@forms/rjsf/deweySubjectMapping";

// API Configuration
export const ISBNDB_API_KEY = process.env.REACT_APP_ISBNDB_API_KEY;
export const ISBNDB_API_URL = process.env.REACT_APP_ISBNDB_API_URL;

/**
 * Validates ISBN format (10 or 13 digits)
 * @param {string} isbn - The ISBN to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateISBN = (isbn) => {
  const cleanISBN = isbn.replace(/[-\s]/g, "");
  return /^\d{10}$/.test(cleanISBN) || /^\d{13}$/.test(cleanISBN);
};

/**
 * Fetches book data from ISBNDB API
 * @param {string} isbn - The ISBN to lookup
 * @param {string} apiKey - The API key
 * @param {string} apiUrl - The API URL
 * @returns {Promise<Object>} - The book data
 */
export const fetchBookDataFromAPI = async (isbn, apiKey, apiUrl) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000);

  try {
    const response = await fetch(`${apiUrl}/book/${isbn}`, {
      headers: {
        Authorization: apiKey,
        "Content-Type": "application/json",
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(getAPIErrorMessage(response.status, response.statusText));
    }

    const data = await response.json();

    if (!data.book) {
      throw new Error("No book data found in response");
    }

    return data.book;
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error(
        "Request timeout. The API is taking too long to respond. Please try again."
      );
    } else if (
      error.message.includes("Failed to fetch") ||
      error.message.includes("NetworkError")
    ) {
      throw new Error(
        "Network error. Please check your internet connection and try again."
      );
    }
    throw error;
  }
};

/**
 * Gets appropriate error message based on API response status
 * @param {number} status - HTTP status code
 * @param {string} statusText - HTTP status text
 * @returns {string} - User-friendly error message
 */
export const getAPIErrorMessage = (status, statusText) => {
  switch (status) {
    case 404:
      return "Book not found with this ISBN";
    case 401:
      return "Invalid API key or unauthorized access";
    case 429:
      return "API rate limit exceeded. Please try again later";
    case 403:
      return "Access forbidden. Please check your API key permissions";
    default:
      return `API error: ${status} - ${statusText}`;
  }
};

/**
 * Generates Dewey Decimal call number from book data
 * @param {Object} bookData - The book data from API
 * @returns {string} - Generated call number
 */
export const generateCallNumber = (bookData) => {
  try {
    // 1. Determine subject classification number
    let deweyNumber = "000"; // Default to general knowledge

    // Check subjects/keywords for Dewey classification
    if (bookData.subjects && bookData.subjects.length > 0) {
      for (const subject of bookData.subjects) {
        const subjectLower = subject.toLowerCase();
        for (const [keyword, dewey] of Object.entries(DEWEY_SUBJECT_MAPPING)) {
          if (subjectLower.includes(keyword)) {
            deweyNumber = dewey;
            break;
          }
        }
        if (deweyNumber !== "000") break;
      }
    }

    // Also check in title for subject hints
    if (deweyNumber === "000" && bookData.title) {
      const titleLower = bookData.title.toLowerCase();
      for (const [keyword, dewey] of Object.entries(DEWEY_SUBJECT_MAPPING)) {
        if (titleLower.includes(keyword)) {
          deweyNumber = dewey;
          break;
        }
      }
    }

    // 2. Generate Cutter number from author's last name
    let cutterNumber = "";
    if (bookData.authors && bookData.authors.length > 0) {
      const authorName = bookData.authors[0];
      const nameParts = authorName.split(" ");
      const lastName = nameParts[nameParts.length - 1];

      // Simple Cutter number generation (first letter + numbers)
      const firstLetter = lastName.charAt(0).toUpperCase();
      const restOfName = lastName.substring(1).toLowerCase();

      // Generate numeric part based on subsequent letters
      let numericPart = "";
      for (let i = 0; i < Math.min(restOfName.length, 3); i++) {
        const charCode = restOfName.charCodeAt(i);
        // Convert to a number between 1-99
        numericPart += Math.floor(((charCode - 97) / 25) * 99)
          .toString()
          .padStart(2, "0");
      }

      cutterNumber = firstLetter + numericPart.substring(0, 3);
    }

    // 3. Add publication year
    let year = "";
    if (bookData.date_published) {
      const pubYear = new Date(bookData.date_published).getFullYear();
      if (!isNaN(pubYear)) {
        year = pubYear.toString();
      }
    }

    // 4. Construct the call number
    let callNumber = deweyNumber;
    if (cutterNumber) {
      callNumber += " " + cutterNumber;
    }
    if (year) {
      callNumber += " " + year;
    }

    return callNumber;
  } catch (error) {
    return "000"; // Fallback to general classification
  }
};

/**
 * Maps common book data fields from API response
 * @param {Object} bookData - The book data from API
 * @returns {Object} - Common mapped fields
 */
export const mapCommonBookData = (bookData) => {
  const mappedData = {};

  // Title
  if (bookData.title) {
    mappedData.title = bookData.title;
  }

  // Authors
  if (bookData.authors && bookData.authors.length > 0) {
    mappedData.authors = bookData.authors.map((author) => ({
      full_name: author,
      type: "PERSON",
    }));
  }

  // Publication year
  if (bookData.date_published) {
    const year = new Date(bookData.date_published).getFullYear();
    if (!isNaN(year)) {
      mappedData.publication_year = year.toString();
    }
  }

  // Publisher information
  if (bookData.publisher) {
    mappedData.imprint = {
      publisher: bookData.publisher,
    };

    if (bookData.publisher_place) {
      mappedData.imprint.place = bookData.publisher_place;
    }
  }

  // ISBN as identifier
  const isbnValue = bookData.isbn13 || bookData.isbn;
  if (isbnValue) {
    mappedData.identifiers = [
      {
        scheme: "ISBN",
        value: isbnValue,
      },
    ];
  }

  // Pages
  if (bookData.pages) {
    mappedData.number_of_pages = parseInt(bookData.pages, 10);
  }

  // Subjects/Keywords
  if (bookData.subjects && bookData.subjects.length > 0) {
    mappedData.subjects = bookData.subjects.slice(0, 3).map((subject) => ({
      value: subject,
      scheme: "DEWEY",
    }));
  }

  // Edition
  if (bookData.edition) {
    mappedData.edition = bookData.edition;
  }

  // Synopsis/Abstract
  if (bookData.synopsis) {
    mappedData.abstract = bookData.synopsis;
  }

  // Physical description
  if (bookData.dimensions) {
    mappedData.physical_description = bookData.dimensions;
  }

  return mappedData;
};

/**
 * Creates internal notes from book data
 * @param {Object} bookData - The book data from API
 * @returns {string} - Formatted internal notes
 */
export const createInternalNotes = (bookData) => {
  let internalNotes = "";

  if (bookData.title) {
    internalNotes += `Title: ${bookData.title}\n`;
  }
  if (bookData.authors && bookData.authors.length > 0) {
    internalNotes += `Author(s): ${bookData.authors.join(", ")}\n`;
  }
  if (bookData.publisher) {
    internalNotes += `Publisher: ${bookData.publisher}\n`;
  }
  if (bookData.date_published) {
    internalNotes += `Publication Date: ${bookData.date_published}\n`;
  }
  if (bookData.synopsis) {
    internalNotes += `Synopsis: ${bookData.synopsis}\n`;
  }

  return internalNotes.trim();
};

/**
 * Validates API configuration
 * @param {string} apiKey - The API key to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateAPIConfig = (apiKey) => {
  return apiKey && apiKey.trim() !== "";
};
